import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Button, Typography, IconButton, Input, Tooltip, Dialog, DialogActions, DialogContent, DialogTitle, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { jobServices } from '../../services/jobServices';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../services/authentication/authContext';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid } from '@mui/x-data-grid';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { jwtDecode } from 'jwt-decode';
import '../../css/Button.css';
import '../../css/DataTable.css';
import '../../css/Dialog.css';
import ConfirmationDialog from '../common/ConfirmationDialog';
import TableSkeleton from '../common/TableSkeleton';
import EmptyState from '../common/EmptyState';
import ErrorState from '../common/ErrorState';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import { downloadReport } from '../../utils/downloadReport ';
import { Edit, Delete, Add, Download, Visibility, GetApp } from '@mui/icons-material';
import PageHeader from '../ui/PageHeader';
import { useSnackbar } from '../../utils/useSnackbar';
import ActionProgressDialog from '../common/ActionProgressDialog';

const Jobs = () => {
  const [jobsData, setJobsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchJob, setSearchJob] = useState('');
  const [debouncedSearchJob, setDebouncedSearchJob] = useState(searchJob);
  const [columnFilter, setColumnFilter] = useState('');
  const isSearchFirstRender = useRef(true);
  const isFilterFirstRender = useRef(true);
  const isSortFirstRender = useRef(true);
  const [debouncedFilterJob, setDebouncedFilterJob] = useState(columnFilter);
  const [sortModel, setSortModel] = useState([{ field: 'updatedAt', sort: 'desc' }]);
  const [openDialog, setOpenDialog] = useState(false);
  const [jobToDelete, setJobToDelete] = useState(null);
  const navigate = useNavigate();
  const { accessToken } = useAuth();
  const { t } = useTranslation();
  const navigationUrl = "/home/job";
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const pageSizeOptions = [25, 50, 100];
  const { showSnackbar, SnackbarComponent } = useSnackbar();
  const [roles, setRoles] = useState([]);
  const isSuperAdminOrAdmin = Array.isArray(roles) && (roles.includes('ADMIN') || roles.includes('SUPER_ADMIN'));
  const isSubAdmin = Array.isArray(roles) && roles.includes('SUB_ADMIN');
  const [openDialogFileUpload, setOpenDialogFileUpload] = useState(false);
  const [fileUpload, setFileUpload] = useState();
  const [fileName, setFileName] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const theme = useTheme();
  const isTabletOrBelow = useMediaQuery(theme.breakpoints.down('lg'));
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const clickedField = sortModel?.[0]?.field || 'updatedAt';
      const col = columns.find(c => c.field === clickedField);
      const sortField = col?.sortField || clickedField;
      const sortDir = sortModel[0]?.sort || "desc";

      const response = await jobServices.getCurrentJobsForStatusUpdate(paginationModel.page, paginationModel.pageSize, debouncedSearchJob, debouncedFilterJob, true, sortField, sortDir);
      setJobsData(response.content);
      setRowCount(response.totalElements);

    } catch (error) {
      if (error.response && error.response.status === 404) {
        setJobsData([]);
        setRowCount(0);
      } else {
        console.error("Error fetching data", error);
        setError('Failed to load jobs. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Decode roles separately — only when accessToken changes
  useEffect(() => {
    if (accessToken) {
      const decodedToken = jwtDecode(accessToken);
      let extractedRoles = [];
      if (Array.isArray(decodedToken.roles)) {
        extractedRoles = decodedToken.roles;
      } else if (typeof decodedToken.roles === "string") {
        extractedRoles = decodedToken.roles.split(",").map(role => role.trim());
      }
      setRoles(extractedRoles);
    } else {
      navigate("/login");
    }
  }, [accessToken]);

  // Fetch data when pagination, search, filter, or sort changes
  useEffect(() => {
    if (accessToken) {
      fetchData();
    }
  }, [accessToken, paginationModel, debouncedSearchJob, debouncedFilterJob, sortModel]);

  useEffect(() => {
    const message = localStorage.getItem("successMessage");
    if (message) {
      showSnackbar(message, 'success');
      localStorage.removeItem("successMessage");
    }
  }, []);

  useEffect(() => {
    if (isSearchFirstRender.current) {
      isSearchFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const value = searchJob.trim();

      if (value === "" || value.length > 2) {
        setDebouncedSearchJob(value);
        setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchJob]);

  useEffect(() => {
    if (isFilterFirstRender.current) {
      isFilterFirstRender.current = false;
      return;
    }
    const timer = setTimeout(() => {
      const value = columnFilter.trim();

      if (value === "" || value.length > 2) {
        setDebouncedFilterJob(value);
        setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [columnFilter]);

  const handleAddJob = () => {
    if (accessToken) {
      navigate(navigationUrl);
    }
  };

  const handleEditJob = (job) => {
    if (accessToken) {
      navigate(navigationUrl, { state: { jobData: job } });
    }
  };

  // Columns for the job table
  const columns = useMemo(() => [
    {
      field: 'name', headerName: "Job", sortField: 'name', flex: 1, minWidth: isMobile ? 150 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value || '-'}</span></Tooltip>
      )
    },
    {
      field: "clientName", headerName: "Client", sortField: 'client.name', flex: 1, minWidth: isMobile ? 150 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value || '-'}</span></Tooltip>
      )
    },
    {
      field: 'createdByFirstName', headerName: "Created By", sortField: 'createdBy.firstName', flex: 1, minWidth: isMobile ? 150 : 0, renderCell: (params) => (
        <Tooltip title={`${params.row.createdByFirstName || ''} ${params.row.createdByLastName || ''}`.trim()}>
          <span>{`${params.row.createdByFirstName || ''} ${params.row.createdByLastName || ''}`.trim() || '-'}</span>
        </Tooltip>
      )
    },
    {
      field: 'status', headerName: "Status", sortField: 'status', flex: 1, minWidth: isMobile ? 120 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value || '-'}</span></Tooltip>
      )
    },
    {
      field: 'type', headerName: t('job.job_type'), sortField: 'type', flex: 1, minWidth: isMobile ? 120 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value || '-'}</span></Tooltip>
      )
    },
    {
      field: 'createDate', headerName: t('job.create_date'), sortField: 'createDate', flex: 1, minWidth: isMobile ? 120 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value || '-'}</span></Tooltip>
      )
    },
    {
      field: 'expectedEndDate', headerName: t('job.end_date'), sortField: 'expectedEndDate', flex: 1, minWidth: isMobile ? 120 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value || '-'}</span></Tooltip>
      )
    },
    {
      field: 'budgetHours', headerName: t('job.budget_hours'), sortField: 'budgetHours', flex: 1, minWidth: isMobile ? 100 : 0, renderCell: (params) => (
        <Tooltip title={params.value}><span>{params.value || '-'}</span></Tooltip>
      )
    },
    ...((isSuperAdminOrAdmin || isSubAdmin) ? [
      {
        field: 'actions',
        headerName: t('job.actions'),
        flex: 1,
        minWidth: isMobile ? 100 : 0,
        renderCell: (params) => (
          <div>
            <IconButton
              color="primary"
              onClick={() => handleEditJob(params.row)}
              className="icon-button"
            >
              <EditIcon fontSize="small" />
            </IconButton>

            <IconButton
              color="secondary"
              onClick={() => handleDeleteJob(params.row.id)}
              className="icon-button"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </div>
        ),
      }
    ] : []),
  ], [isMobile, t, isSuperAdminOrAdmin, isSubAdmin]);

  const filteredDataMemo = useMemo(() => {
    if (!jobsData) return [];

    const filter = (job, search) => {
      const lowerSearch = search?.trim().toLowerCase() || '';
      return (
        (job?.name?.toLowerCase().includes(lowerSearch)) ||
        (job?.clientName?.toLowerCase().includes(lowerSearch)) ||
        (job?.status?.toLowerCase().includes(lowerSearch)) ||
        (job?.type?.toLowerCase().includes(lowerSearch)) ||
        (job?.createDate?.toLowerCase().includes(lowerSearch)) ||
        (job?.expectedEndDate?.toLowerCase().includes(lowerSearch)) ||
        (job?.createdByFirstName?.toLowerCase().includes(lowerSearch)) ||
        (job?.createdByLastName?.toLowerCase().includes(lowerSearch)) ||
        `${job?.createdByFirstName} ${job?.createdByLastName}`.toLowerCase().includes(lowerSearch)
      );
    };

    // Apply both filters
    return jobsData
      .filter(job => filter(job, searchJob) && filter(job, columnFilter))
      .map(job => ({
        ...job,
        client: job.clientName,
        createdByUser: job.createdByUser ? `${job.createdByUser.firstName ?? ''} ${job.createdByUser.lastName ?? ''}`.trim() : '-',
        createDate: job.createDate.split(' ')[0],
        expectedEndDate: job.expectedEndDate.split(' ')[0]
      }));

  }, [jobsData, searchJob, columnFilter]);

  const jobsColumnVisibilityModel = useMemo(() => {
    if (isMobile) {
      return {
        createdByFirstName: false,
        // createDate: false,
        // expectedEndDate: false,
        // budgetHours: false,
      };
    }

    if (isTabletOrBelow) {
      return {
        // createDate: false,
        // budgetHours: false,
      };
    }

    return {};
  }, [isMobile, isTabletOrBelow]);

  const handleDeleteJob = (jobId) => {
    setJobToDelete(jobId);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setJobToDelete(null);
  };

  const handleFileUpload = () => {
    setOpenDialogFileUpload(true);
  };

  const handleFileUploadApi = async () => {
    if (!fileUpload || isUploading) return;

    setIsUploading(true);
    setActionMessage('Uploading Bulk Jobs...');
    setLoading(true);

    const formData = new FormData();
    formData.append("file", fileUpload);

    try {
      const response = await jobServices.bulkJobFileUpload(formData);

      if (response === "File processed successfully.") {
        showSnackbar("File uploaded successfully", "success");
        setOpenDialogFileUpload(false);
        setFileUpload(null);
        fetchData();

      } else if (response.startsWith("Error File Name:")) {
        const errorFileName = response.split("Error File Name:")[1].trim();
        setFileName(errorFileName);

        showSnackbar("File uploaded successfully! You can download the error file.", "error");
        setOpenDialogFileUpload(false);
        setFileUpload(null);
        fetchData();
      }

    } catch (error) {
      console.error(error);
      showSnackbar("Error uploading file. Please try again.", "error");
    } finally {
      setIsUploading(false);
      setLoading(false);
    }
  };

  const handleErrorFile = async () => {
    if (fileName) {
      try {
        setIsUploading(true);
        setActionMessage('Downloading Error File...');
        const response = await jobServices.downloadBulkJobErrorFIle(fileName);

        downloadReport(response);
        showSnackbar("Error file is being downloaded.", "success");

        setFileName(null);
      } catch (error) {
        showSnackbar("Error downloading the error file. Please try again.", "error");
      } finally {
        setIsUploading(false);
      }
    } else {
      showSnackbar("No error file to download.", "error");
    }
  }

  const handleBulkJobFormat = async () => {
    try {
      setIsUploading(true);
      setActionMessage('Downloading Format...');
      const response = await jobServices.downloadBulkJobFormat();
      handleCloseDialogFileUpload();
      downloadReport(response);
    } catch (error) {
      console.error("Error downloading bulk job formate");
    } finally {
      setIsUploading(false);
    }
  }

  const handleCloseDialogFileUpload = () => {
    setOpenDialogFileUpload(false);
    setFileUpload(null);
  };

  const handlePaginationModelChange = (newPaginationModel) => {
    setPaginationModel(newPaginationModel);
  };

  const confirmDelete = () => {
    if (jobToDelete) {
      jobServices.deleteJobData(jobToDelete)
        .then(() => {
          setJobsData((prevData) => prevData.filter(job => job.id !== jobToDelete));
          showSnackbar("Job deleted successfully!", "success");
        })
        .catch((error) => {
          showSnackbar("Error deleting job data", "error");
        });
    }
    handleCloseDialog();
  };

  const handleFileUploadChange = (event) => {
    setFileUpload(event.target.files[0]);
  };

  return (
    <div className="data-table-container-job">
      <PageHeader title={t('job.jobs')} />

      {/* Snackbar */}
      {SnackbarComponent}

      <div className="search-and-button-job">
        <Button
          variant="contained"
          onClick={handleAddJob}
          className="add-button"
          startIcon={<AddIcon />}
        >
          {t('job.add')}
        </Button>

        {(isSuperAdminOrAdmin || isSubAdmin) &&
          <Button
            onClick={handleFileUpload}
            className="file-upload-button"
            startIcon={<CloudUploadIcon />}
            variant="outlined"
            color='inherit'
            disabled={fileName !== null}
          >
            Bulk Job Upload
          </Button>}

        {fileName && (
          <Button
            onClick={handleErrorFile}
            className="file-upload-button"
            variant="outlined"
            color="error"
            startIcon={<DownloadIcon />}
          >
            Bulk Job Error File
          </Button>
        )}

        {/* General Search Filter */}
        <div className="search-container-job">
          <input
            type="text"
            placeholder="Type to search..."
            value={searchJob}
            onChange={(e) => setSearchJob(e.target.value)}
            className="search-input-job"
          />
        </div>
      </div>

      <div className="data-table">
        <div className="data-grid-container" style={{ height: '70vh' }}>
          {loading ? (
            <Box sx={{ height: '100%', overflow: 'hidden' }}>
              <TableSkeleton rows={8} columns={6} />
            </Box>
          ) : error ? (
            <Box sx={{ height: '70vh', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <ErrorState
                title="Could not load jobs"
                description="We couldn’t fetch jobs from the server."
                details={typeof error === 'string' ? error : undefined}
                action={
                  <Button
                    variant="contained"
                    onClick={fetchData}
                    sx={{ mt: 1, backgroundColor: 'var(--primary)' }}
                  >
                    Retry
                  </Button>
                }
              />
            </Box>
          ) : filteredDataMemo?.length === 0 ? (
            <Box sx={{ height: '70vh', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EmptyState
                title="No jobs found"
                description="Try clearing filters or adjusting your search."
              />
            </Box>
          ) : (
            <DataGrid
              loading={false}
              rows={filteredDataMemo}
              columns={columns}
              pagination
              paginationMode="server"
              filterMode="server"
              onFilterModelChange={(newFilterModel) => {
                const value = newFilterModel.items?.[0]?.value || '';
                setColumnFilter((prev) => (prev === value ? prev : value));
              }}
              sortingMode="server"
              onSortModelChange={(newModel) => {
                if (isSortFirstRender.current) {
                  isSortFirstRender.current = false;
                  if (!newModel?.length) return;
                }
                const next = newModel?.[0];
                const current = sortModel?.[0];
                if (next?.field === current?.field && next?.sort === current?.sort) return;
                if (!newModel?.length && sortModel.length > 0) {
                  const currentField = sortModel[0].field;
                  const isColumnDefined = columns.some(c => c.field === currentField);

                  if (!isColumnDefined) {
                    return;
                  }
                }
                setSortModel(newModel);
                setPaginationModel(prev => (prev.page === 0 ? prev : { ...prev, page: 0 }));
              }}
              sortModel={sortModel}
              paginationModel={paginationModel}
              rowCount={rowCount}
              pageSizeOptions={pageSizeOptions}
              onPaginationModelChange={handlePaginationModelChange}
              disableSelectionOnClick
              columnVisibilityModel={jobsColumnVisibilityModel}
            />
          )}
        </div>

        {/* Delete Confirmation Dialog */}
        <ConfirmationDialog
          open={openDialog}
          onClose={handleCloseDialog}
          onConfirm={confirmDelete}
          title="Delete Confirmation"
          message="Are you sure you want to delete this job?"
          messageSecond="*Associated Tasks with this job will also be deleted."
          cancelLabel="Cancel"
          confirmLabel="Delete"
          isDelete={true}
        />

        <Dialog
          open={openDialogFileUpload}
          onClose={handleCloseDialogFileUpload}
          slotProps={{ paper: { className: 'modern-dialog' } }}
          maxWidth="xs"
        >
          <DialogTitle className="modern-dialog-title">
            <span className="modern-dialog-title-text">Bulk Job Creation</span>
            <IconButton
              aria-label="close"
              onClick={handleCloseDialogFileUpload}
              className="modern-dialog-close-btn"
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent className="modern-dialog-content">
            <Box sx={{ mt: 1 }}>
              <Input
                id="file-upload"
                type="file"
                accept=".xls, .xlsx"
                onChange={handleFileUploadChange}
                sx={{ display: 'none' }}
              />
              <label htmlFor="file-upload">
                <Box className="file-upload-area">
                  <CloudUploadIcon className="file-upload-icon" />
                  <Typography variant="body1" sx={{ fontWeight: 600, color: '#334155' }}>
                    Click to select file
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
                    Support for .xls, .xlsx files
                  </Typography>

                  {fileUpload && (
                    <Box className="selected-file-name">
                      {fileUpload.name}
                    </Box>
                  )}
                </Box>
              </label>

              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', textAlign: 'center' }}>
                  Please ensure you use the standard template for uploads.
                </Typography>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions className="modern-dialog-actions">
            <Button
              onClick={handleBulkJobFormat}
              variant="outlined"
              sx={{
                borderColor: '#10b981',
                color: '#10b981',
                '&:hover': { borderColor: '#059669', backgroundColor: 'rgba(16, 185, 129, 0.04)' }
              }}
              startIcon={<GetApp />}
            >
              Format
            </Button>
            <Box sx={{ flexGrow: 1 }} />
            <Button
              onClick={handleCloseDialogFileUpload}
              variant="outlined"
              color="inherit"
            >
              Cancel
            </Button>
            <Button
              onClick={handleFileUploadApi}
              color="primary"
              variant='contained'
              disabled={isUploading || !fileUpload}
            >
              {isUploading ? "Uploading..." : "Upload"}
            </Button>
          </DialogActions>
        </Dialog>

        <ActionProgressDialog open={isUploading} message={actionMessage} />

      </div>
    </div>
  );
};

export default Jobs;
